import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LayoutDashboard, Plus } from "@digital-twins/design-system/icons";
import { useAuth } from "@/lib/config/auth-provider";
import { LibraryNavigationRail } from "@/components/library/LibraryNavigationRail";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/Text";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { CreativeBoardNavigationProps } from "@/types/creative-library";
import { LazyPrivateImage } from "./LazyPrivateImage";

export function CreativeBoardNavigation({
  boards,
  activeToken,
  selectedBoard,
  selectedCount,
  creatives,
  loading,
  error,
  onSelect,
  onNew,
  onNewBrand,
  canCreate,
  className,
}: CreativeBoardNavigationProps) {
  const { t } = useTranslation("app");
  const { user } = useAuth();
  const [channel, setChannel] = useState("all");
  const channels = useMemo(
    () =>
      Array.from(new Set(boards.flatMap((board) => board.channelTags))).sort(),
    [boards],
  );
  const catalog =
    selectedBoard &&
    !boards.some((board) => board.token === selectedBoard.token)
      ? [selectedBoard, ...boards]
      : boards;
  const visible = catalog.filter(
    (board) =>
      channel === "all" ||
      board.token === activeToken ||
      board.channelTags.includes(channel),
  );
  const hint =
    !loading && !error && !canCreate
      ? t("creativeLibrary.boardNeedsBrand")
      : undefined;
  const channelFilter = channels.length > 0 && (
    <Select value={channel} onValueChange={setChannel}>
      <SelectTrigger
        aria-label={t("creativeLibrary.filterBoardChannel")}
        data-testid="creative-board-channel"
        className="h-9 w-full text-xs"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{t("creativeLibrary.allChannels")}</SelectItem>
        {channels.map((tag) => (
          <SelectItem key={tag} value={tag}>
            {tag}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
  const rows = visible.map((board) => {
    const preview = creatives.find((item) =>
      board.definition.referenceTokens.includes(item.token),
    );
    return {
      token: board.token,
      name: board.name,
      count:
        board.token === activeToken && selectedCount !== undefined
          ? selectedCount
          : board.creativeCount,
      thumbnail: preview ? (
        <LazyPrivateImage
          source={preview.mediaUrl}
          alt=""
          className="h-full w-full object-cover"
        />
      ) : (
        <span className="flex h-full w-full items-center justify-center rounded-md bg-primary/10 text-primary/70">
          <LayoutDashboard className="h-3.5 w-3.5" aria-hidden />
        </span>
      ),
      testId: `creative-board-row-${board.token}`,
    };
  });
  return (
    <div className={className}>
      <div
        className="flex flex-col gap-3 md:hidden"
        data-testid="creative-board-mobile"
      >
        <div className="flex items-center gap-2">
          <Select
            value={activeToken ?? "all"}
            onValueChange={(value) => onSelect(value === "all" ? null : value)}
            disabled={loading || error}
          >
            <SelectTrigger
              aria-label={t("creativeLibrary.boards")}
              data-testid="creative-board-picker"
              className="min-w-0 flex-1"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t("creativeLibrary.allCreatives")}
              </SelectItem>
              {activeToken &&
                !visible.some((board) => board.token === activeToken) && (
                  <SelectItem value={activeToken}>
                    {t("creativeLibrary.boardUnavailable")}
                  </SelectItem>
                )}
              {rows.map((row) => (
                <SelectItem key={row.token} value={row.token}>
                  {row.name}
                  {row.count !== undefined ? ` · ${row.count}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            onClick={onNew}
            disabled={loading || error || !canCreate}
            aria-label={t("creativeLibrary.newBoard")}
            data-testid="creative-board-mobile-new"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        {channelFilter}
        {hint && (
          <div className="flex items-center justify-between gap-2">
            <Text size="xs" color="muted">
              {hint}
            </Text>
            <Button variant="ghost" size="sm" onClick={onNewBrand}>
              {t("creativeLibrary.newBrand")}
            </Button>
          </div>
        )}
      </div>
      <div className="hidden md:block">
        <LibraryNavigationRail
          title={t("creativeLibrary.boards")}
          allLabel={t("creativeLibrary.allCreatives")}
          newLabel={t("creativeLibrary.newBoard")}
          collapseLabel={t("library.rail.collapse")}
          expandLabel={t("library.rail.expand")}
          storageKey={`creative-boards-rail-collapsed:${user?.id ?? "anonymous"}`}
          activeToken={activeToken}
          isAllActive={!activeToken}
          groups={[
            { key: "boards", label: t("creativeLibrary.savedBoards"), rows },
          ]}
          onSelect={onSelect}
          onSelectAll={() => onSelect(null)}
          onNew={onNew}
          newDisabled={loading || error || !canCreate}
          newHint={hint}
          isLoading={loading}
          hasError={error}
          errorLabel={t("creativeLibrary.loadFailed")}
          testIdPrefix="creative-boards-rail"
          filters={<div className="pt-3">{channelFilter}</div>}
        />
        {hint && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2"
            onClick={onNewBrand}
            data-testid="creative-board-new-brand"
          >
            {t("creativeLibrary.newBrand")}
          </Button>
        )}
      </div>
    </div>
  );
}
